import type { ReactNode } from "react";
import type { RuntimeBillingPlan } from "../lib/api/runtimeWorkspaceClient";

type Language = "de" | "en";

export function dealWinPlanCopy(planId: string, language: Language) {
  const copy = {
    de: {
      deal_win_engine_single_12m: {
        title: "Deal Win Engine · Einzelzugang",
        description: "Für Vertriebsverantwortliche, die einzelne Opportunities sauber bewerten und den nächsten Schritt klar priorisieren wollen."
      },
      deal_win_engine_team3_12m: {
        title: "Deal Win Engine · Team 3",
        description: "Für kleine Vertriebsteams, die Deal-Reviews vereinheitlichen und gute Chancen gemeinsam fokussierter steuern wollen."
      },
      deal_win_engine_team5_12m: {
        title: "Deal Win Engine · Team 5",
        description: "Für aktive Vertriebseinheiten, die Pipeline-Qualität, Risiken und nächste Schritte regelmäßig gemeinsam schärfen."
      },
      deal_win_engine_team10_12m: {
        title: "Deal Win Engine · Team 10",
        description: "Für größere Vertriebsorganisationen, die Opportunity-Steuerung skalieren und eine einheitliche Deal-Qualität etablieren wollen."
      }
    },
    en: {
      deal_win_engine_single_12m: {
        title: "Deal Win Engine · Individual Access",
        description: "For sales leaders who want to assess individual opportunities clearly and prioritise the next step with confidence."
      },
      deal_win_engine_team3_12m: {
        title: "Deal Win Engine · Team 3",
        description: "For small sales teams that want consistent deal reviews and sharper focus on the opportunities worth pursuing."
      },
      deal_win_engine_team5_12m: {
        title: "Deal Win Engine · Team 5",
        description: "For active sales units that regularly sharpen pipeline quality, risks, and next actions together."
      },
      deal_win_engine_team10_12m: {
        title: "Deal Win Engine · Team 10",
        description: "For larger sales organisations that want to scale opportunity steering and establish consistent deal quality."
      }
    }
  } as const;
  return copy[language][planId as keyof typeof copy.de] ?? {
    title: "Deal Win Engine",
    description: ""
  };
}

function formatEuro(value: number, language: Language): string {
  return new Intl.NumberFormat(language === "en" ? "en-US" : "de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0
  }).format(value);
}

function dealWinMonthlyPriceText(value: number, language: Language): string {
  return formatEuro(Math.round(value / 12), language);
}

function dealWinAnnualEquivalentText(value: number, language: Language): string {
  return language === "en"
    ? `billed monthly · equals ${formatEuro(value, language)} net per year`
    : `monatlich abgerechnet · entspricht ${formatEuro(value, language)} netto pro Jahr`;
}

function dealWinExtraUserMonthlyText(value: number | undefined, language: Language): string | undefined {
  if (!value || value <= 0) return undefined;
  return language === "en"
    ? `Additional user: ${dealWinMonthlyPriceText(value, language)} net / month`
    : `Zusätzlicher Zugang: ${dealWinMonthlyPriceText(value, language)} netto / Monat`;
}

export function GuidedPlanCard({
  className = "",
  badge,
  title,
  description,
  price,
  priceSuffix,
  features,
  addonConfig,
  note,
  action
}: {
  className?: string;
  badge?: ReactNode;
  title: ReactNode;
  description: ReactNode;
  price: ReactNode;
  priceSuffix: ReactNode;
  features: ReactNode;
  addonConfig?: ReactNode;
  note?: ReactNode;
  action: ReactNode;
}) {
  return (
    <article className={`billing-plan-card ${className}`.trim()} data-pricing-card="guided">
      <div className="billing-plan-card__badge-row">{badge}</div>
      <h3 className="billing-plan-card__title">{title}</h3>
      <p className="billing-plan-card__description">{description}</p>
      <p className="billing-plan-card__price"><strong>{price}</strong> <span>{priceSuffix}</span></p>
      <div className="billing-plan-card__features">{features}</div>
      {addonConfig ? <div className="billing-plan-card__addon">{addonConfig}</div> : null}
      {note ? <p className="billing-plan-card__note">{note}</p> : null}
      <div className="billing-plan-card__action">{action}</div>
    </article>
  );
}

export function DealWinPlanCard({
  plan,
  language,
  selected,
  singleBadge,
  bundleBadge,
  includedUsersLabel,
  durationLabel,
  intervalLabel,
  note,
  addonConfig,
  action
}: {
  plan: RuntimeBillingPlan;
  language: Language;
  selected: boolean;
  singleBadge: ReactNode;
  bundleBadge: ReactNode;
  includedUsersLabel: ReactNode;
  durationLabel: ReactNode;
  intervalLabel: ReactNode;
  note: ReactNode;
  addonConfig?: ReactNode;
  action: ReactNode;
}) {
  const planCopy = dealWinPlanCopy(plan.id, language);
  const extraUserText = dealWinExtraUserMonthlyText(plan.extraUserPriceNetEur, language);
  return (
    <article className={`deal-win-pricing-card ${selected ? "deal-win-pricing-card-selected" : ""}`} data-pricing-card="deal-win">
      <div className="deal-win-pricing-card__main">
        {plan.id === "deal_win_engine_single_12m" ? singleBadge : bundleBadge}
        <h3 className="deal-win-pricing-card__title">{planCopy.title}</h3>
        <p className="deal-win-pricing-card__description">{planCopy.description}</p>
      </div>
      <div className="deal-win-pricing-card__aside">
        <p className="deal-win-pricing-card__price">
          <strong>{dealWinMonthlyPriceText(plan.priceNetEur, language)}</strong>
          <span>{language === "en" ? "net / month" : "netto / Monat"}</span>
          <small>{dealWinAnnualEquivalentText(plan.priceNetEur, language)}</small>
        </p>
        <ul className="deal-win-pricing-card__facts">
          <li>{plan.includedUsers ?? 1} {includedUsersLabel}</li>
          {extraUserText ? <li>{extraUserText}</li> : null}
          <li>{plan.durationMonths ? `${plan.durationMonths} ${durationLabel}` : intervalLabel}</li>
        </ul>
        {addonConfig ? <div className="deal-win-pricing-card__addon">{addonConfig}</div> : null}
        <p className="deal-win-pricing-card__note">{note}</p>
        <div className="deal-win-pricing-card__action">{action}</div>
      </div>
    </article>
  );
}
